import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { DarkPage } from "@/components/app/PageHeader";
import { Plus, Truck, Building2, X, ChevronDown, Pencil, Trash2, Receipt } from "lucide-react";

const STATUS_STYLES = {
  draft: "text-[#7d8590] bg-[#1c2128] border-[#30363d]",
  sent: "text-blue-400 bg-blue-500/10 border-blue-500/20",
  received: "text-[#00F5D4] bg-[#00F5D4]/10 border-[#00F5D4]/20",
  cancelled: "text-rose-400 bg-rose-500/10 border-rose-500/20",
};

const STATUSES = ["draft", "sent", "received", "cancelled"];

function fmt(n) {
  return `£${(Number(n) || 0).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export default function PurchaseOrders() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("pos");
  const [converting, setConverting] = useState(false);
  const [pos, setPos] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [detail, setDetail] = useState(null);
  const [showPoModal, setShowPoModal] = useState(false);
  const [showSupModal, setShowSupModal] = useState(false);
  const [poForm, setPoForm] = useState({ supplier_id: "", project_id: "", issue_date: new Date().toISOString().slice(0, 10), expected_date: "", notes: "" });
  const [supForm, setSupForm] = useState({ name: "", email: "", phone: "", address: "", currency: "GBP", payment_terms: "" });
  const [editSup, setEditSup] = useState(null);
  const [saving, setSaving] = useState(false);
  const [newLine, setNewLine] = useState({ description: "", quantity: "1", unit_price: "" });

  const load = useCallback(async () => {
    try {
      const [poRes, supRes, pRes] = await Promise.all([
        api.get("/purchase-orders"),
        api.get("/suppliers"),
        api.get("/projects").catch(() => ({ data: [] })),
      ]);
      setPos(poRes.data || []);
      setSuppliers(supRes.data || []);
      setProjects(pRes.data || []);
    } catch {
      toast.error("Failed to load data");
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const loadDetail = async (po) => {
    try {
      const res = await api.get(`/purchase-orders/${po.po_id}`);
      setDetail(res.data);
    } catch {
      toast.error("Failed to load PO");
    }
  };

  const createPo = async () => {
    if (!poForm.supplier_id || !poForm.issue_date) { toast.error("Supplier and issue date required"); return; }
    setSaving(true);
    try {
      const res = await api.post("/purchase-orders", poForm);
      toast.success("PO created");
      setShowPoModal(false);
      setPoForm({ supplier_id: "", project_id: "", issue_date: new Date().toISOString().slice(0, 10), expected_date: "", notes: "" });
      load();
      loadDetail(res.data);
    } catch {
      toast.error("Failed to create PO");
    } finally {
      setSaving(false);
    }
  };

  const updateStatus = async (status) => {
    try {
      await api.put(`/purchase-orders/${detail.po_id}`, { status });
      setDetail(d => ({ ...d, status }));
      load();
    } catch {
      toast.error("Failed to update status");
    }
  };

  const reloadDetail = async (po_id) => {
    const res = await api.get(`/purchase-orders/${po_id}`);
    setDetail(res.data);
  };

  const addLine = async () => {
    if (!newLine.description || !newLine.unit_price) return;
    try {
      await api.post(`/purchase-orders/${detail.po_id}/line-items`, {
        description: newLine.description,
        quantity: parseFloat(newLine.quantity) || 1,
        unit_price: parseFloat(newLine.unit_price) || 0,
      });
      setNewLine({ description: "", quantity: "1", unit_price: "" });
      await reloadDetail(detail.po_id);
    } catch {
      toast.error("Failed to add line item");
    }
  };

  const deleteLine = async (line) => {
    try {
      await api.delete(`/purchase-orders/${detail.po_id}/line-items/${line.po_line_id}`);
      await reloadDetail(detail.po_id);
    } catch {
      toast.error("Failed to delete line item");
    }
  };

  const deletePo = async () => {
    if (!window.confirm(`Delete ${detail.po_number}? This cannot be undone.`)) return;
    try {
      await api.delete(`/purchase-orders/${detail.po_id}`);
      toast.success("Purchase order deleted");
      setDetail(null);
      load();
    } catch {
      toast.error("Failed to delete PO");
    }
  };

  const convertToBill = async () => {
    setConverting(true);
    try {
      const { data } = await api.post(`/purchase-orders/${detail.po_id}/convert-to-bill`);
      toast.success(`Bill ${data.bill_number} created from this PO`);
      navigate("/bills");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to convert PO to bill");
    } finally {
      setConverting(false);
    }
  };

  const saveSup = async () => {
    if (!supForm.name) { toast.error("Supplier name required"); return; }
    setSaving(true);
    try {
      if (editSup) {
        await api.put(`/suppliers/${editSup.supplier_id}`, supForm);
        toast.success("Supplier updated");
      } else {
        await api.post("/suppliers", supForm);
        toast.success("Supplier created");
      }
      setShowSupModal(false);
      setSupForm({ name: "", email: "", phone: "", address: "", currency: "GBP", payment_terms: "" });
      setEditSup(null);
      load();
    } catch {
      toast.error("Failed to save supplier");
    } finally {
      setSaving(false);
    }
  };

  const deleteSup = async (s) => {
    if (!window.confirm(`Delete ${s.name}?`)) return;
    try {
      await api.delete(`/suppliers/${s.supplier_id}`);
      toast.success("Deleted");
      load();
    } catch {
      toast.error("Failed to delete supplier");
    }
  };

  const supName = (id) => suppliers.find(s => s.supplier_id === id)?.name || id;
  const projName = (id) => projects.find(p => p.project_id === id)?.name || id;

  if (detail) {
    return (
      <DarkPage><div className="p-6 max-w-5xl mx-auto">
        <button onClick={() => setDetail(null)} className="text-xs text-[#7d8590] hover:text-white mb-4 flex items-center gap-1">
          <ChevronDown className="w-3 h-3 rotate-90" /> Back
        </button>
        <div className="flex items-start justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-white">{detail.po_number}</h1>
            <p className="text-sm text-[#7d8590] mt-1">{supName(detail.supplier_id)} · Issued {detail.issue_date}</p>
          </div>
          <div className="flex items-center gap-2">
            <select value={detail.status} onChange={e => updateStatus(e.target.value)}
              className={`text-xs px-3 py-1.5 rounded-full border ${STATUS_STYLES[detail.status] || STATUS_STYLES.draft} bg-transparent cursor-pointer`}>
              {STATUSES.map(s => <option key={s} value={s} className="bg-[#0d1117]">{s}</option>)}
            </select>
            {detail.status === "received" && (
              <button onClick={convertToBill} disabled={converting}
                className="text-xs px-3 py-1.5 rounded-full border border-[#00F5D4]/30 text-[#00F5D4] bg-[#00F5D4]/10 hover:bg-[#00F5D4]/20 transition-colors flex items-center gap-1">
                <Receipt className="w-3 h-3" /> {converting ? "Converting…" : "Convert to bill"}
              </button>
            )}
            <button onClick={deletePo} className="text-xs px-3 py-1.5 rounded-full border border-rose-500/20 text-rose-400 bg-rose-500/10 hover:bg-rose-500/20 transition-colors flex items-center gap-1">
              <Trash2 className="w-3 h-3" /> Delete
            </button>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-4 mb-6">
          {[["Supplier", supName(detail.supplier_id)], ["Project", detail.project_id ? projName(detail.project_id) : "—"], ["Total", fmt(detail.total)]].map(([k, v]) => (
            <div key={k} className="bg-[#0d1117] border border-[#21262d] rounded-lg p-4">
              <div className="text-xs text-[#7d8590] mb-1">{k}</div>
              <div className="text-white font-semibold">{v}</div>
            </div>
          ))}
        </div>

        {detail.notes && (
          <div className="bg-[#0d1117] border border-[#21262d] rounded-lg p-4 mb-4">
            <div className="text-xs text-[#7d8590] mb-1">Notes</div>
            <p className="text-sm text-white whitespace-pre-wrap">{detail.notes}</p>
          </div>
        )}

        <div className="bg-[#0d1117] border border-[#21262d] rounded-lg overflow-hidden mb-4">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[#21262d]">
                <th className="text-left px-4 py-3 text-xs text-[#7d8590] font-medium">Description</th>
                <th className="text-right px-4 py-3 text-xs text-[#7d8590] font-medium">Qty</th>
                <th className="text-right px-4 py-3 text-xs text-[#7d8590] font-medium">Unit price</th>
                <th className="text-right px-4 py-3 text-xs text-[#7d8590] font-medium">Total</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {(detail.line_items || []).map(line => (
                <tr key={line.po_line_id} className="border-b border-[#21262d] group">
                  <td className="px-4 py-3 text-white">{line.description}</td>
                  <td className="px-4 py-3 text-right text-[#7d8590]">{line.quantity}</td>
                  <td className="px-4 py-3 text-right text-[#7d8590]">{fmt(line.unit_price)}</td>
                  <td className="px-4 py-3 text-right text-white font-medium">{fmt(line.total)}</td>
                  <td className="px-4 py-3 text-right">
                    <button onClick={() => deleteLine(line)} className="opacity-0 group-hover:opacity-100 text-[#484f58] hover:text-rose-400">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
              <tr className="border-b border-[#21262d] bg-[#0a0d12]">
                <td className="px-4 py-2">
                  <input value={newLine.description} onChange={e => setNewLine(v => ({ ...v, description: e.target.value }))}
                    placeholder="New line item…"
                    className="bg-transparent text-white text-sm w-full focus:outline-none placeholder:text-[#484f58]" />
                </td>
                <td className="px-4 py-2">
                  <input value={newLine.quantity} onChange={e => setNewLine(v => ({ ...v, quantity: e.target.value }))} type="number" min="1"
                    className="bg-transparent text-[#7d8590] text-sm text-right w-16 focus:outline-none" />
                </td>
                <td className="px-4 py-2">
                  <input value={newLine.unit_price} onChange={e => setNewLine(v => ({ ...v, unit_price: e.target.value }))} type="number" placeholder="0.00"
                    className="bg-transparent text-[#7d8590] text-sm text-right w-24 focus:outline-none placeholder:text-[#484f58]" />
                </td>
                <td className="px-4 py-2 text-right text-[#484f58] text-sm">{fmt((parseFloat(newLine.quantity) || 1) * (parseFloat(newLine.unit_price) || 0))}</td>
                <td className="px-4 py-2 text-right">
                  <button onClick={addLine} className="text-[#00F5D4] text-xs hover:underline">Add</button>
                </td>
              </tr>
            </tbody>
            <tfoot>
              <tr>
                <td colSpan={3} className="px-4 py-3 text-right text-sm font-semibold text-white">Total</td>
                <td className="px-4 py-3 text-right text-sm font-bold text-white">{fmt(detail.total)}</td>
                <td />
              </tr>
            </tfoot>
          </table>
        </div>
      </div></DarkPage>
    );
  }

  return (
    <DarkPage><div className="p-6 max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white">Purchase Orders</h1>
          <p className="text-sm text-[#7d8590] mt-1">Supplier management and procurement tracking</p>
        </div>
        <div className="flex gap-2">
          {tab === "suppliers" ? (
            <Button onClick={() => { setEditSup(null); setSupForm({ name: "", email: "", phone: "", address: "", currency: "GBP", payment_terms: "" }); setShowSupModal(true); }}
              className="bg-[#00F5D4] text-[#030712] hover:bg-[#00F5D4]/90 font-semibold">
              <Plus className="w-4 h-4 mr-1" /> New Supplier
            </Button>
          ) : (
            <Button onClick={() => { if (suppliers.length === 0) { toast.error("Add a supplier first — switch to the Suppliers tab"); setTab("suppliers"); return; } setShowPoModal(true); }} className="bg-[#00F5D4] text-[#030712] hover:bg-[#00F5D4]/90 font-semibold">
              <Plus className="w-4 h-4 mr-1" /> New PO
            </Button>
          )}
        </div>
      </div>

      <div className="flex bg-[#0d1117] border border-[#21262d] rounded-lg overflow-hidden w-fit mb-6">
        {[["pos", "Purchase Orders", Truck], ["suppliers", "Suppliers", Building2]].map(([key, label, Icon]) => (
          <button key={key} onClick={() => setTab(key)}
            className={`flex items-center gap-2 px-4 py-2 text-sm transition-colors ${tab === key ? "bg-[#1c2128] text-white font-medium" : "text-[#7d8590] hover:text-white"}`}>
            <Icon className="w-4 h-4" />{label}
          </button>
        ))}
      </div>

      {tab === "pos" ? (
        pos.length === 0 ? (
          <div className="text-center py-20 text-[#7d8590] text-sm">No purchase orders yet.</div>
        ) : (
          <div className="bg-[#0d1117] border border-[#21262d] rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[#21262d]">
                  {["PO #", "Supplier", "Project", "Status", "Issued", "Total"].map(h => (
                    <th key={h} className="text-left px-4 py-3 text-xs text-[#7d8590] font-medium">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {pos.map(po => (
                  <tr key={po.po_id} onClick={() => loadDetail(po)} className="border-b border-[#21262d] hover:bg-[#161b22] cursor-pointer transition-colors">
                    <td className="px-4 py-3 text-[#00F5D4] font-mono font-medium">{po.po_number}</td>
                    <td className="px-4 py-3 text-white">{supName(po.supplier_id)}</td>
                    <td className="px-4 py-3 text-[#7d8590]">{po.project_id ? projName(po.project_id) : "—"}</td>
                    <td className="px-4 py-3">
                      <span className={`text-xs px-2 py-0.5 rounded-full border ${STATUS_STYLES[po.status] || STATUS_STYLES.draft}`}>{po.status}</span>
                    </td>
                    <td className="px-4 py-3 text-[#7d8590]">{po.issue_date}</td>
                    <td className="px-4 py-3 text-white font-medium">{fmt(po.total)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      ) : (
        suppliers.length === 0 ? (
          <div className="text-center py-20 text-[#7d8590] text-sm">No suppliers yet.</div>
        ) : (
          <div className="bg-[#0d1117] border border-[#21262d] rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[#21262d]">
                  {["Name", "Email", "Phone", "Currency", "Payment terms", ""].map(h => (
                    <th key={h} className="text-left px-4 py-3 text-xs text-[#7d8590] font-medium">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {suppliers.map(s => (
                  <tr key={s.supplier_id} className="border-b border-[#21262d] group">
                    <td className="px-4 py-3 text-white font-medium">{s.name}</td>
                    <td className="px-4 py-3 text-[#7d8590]">{s.email || "—"}</td>
                    <td className="px-4 py-3 text-[#7d8590]">{s.phone || "—"}</td>
                    <td className="px-4 py-3 text-[#7d8590]">{s.currency}</td>
                    <td className="px-4 py-3 text-[#7d8590]">{s.payment_terms || "—"}</td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex gap-2 justify-end opacity-0 group-hover:opacity-100">
                        <button onClick={() => { setEditSup(s); setSupForm({ name: s.name, email: s.email || "", phone: s.phone || "", address: s.address || "", currency: s.currency, payment_terms: s.payment_terms || "" }); setShowSupModal(true); }}
                          className="text-[#484f58] hover:text-white"><Pencil className="w-4 h-4" /></button>
                        <button onClick={() => deleteSup(s)} className="text-[#484f58] hover:text-rose-400"><Trash2 className="w-4 h-4" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      )}

      {/* PO modal */}
      {showPoModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
          <div className="bg-[#0d1117] border border-[#21262d] rounded-xl w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-white font-semibold">New Purchase Order</h2>
              <button onClick={() => setShowPoModal(false)} className="text-[#7d8590] hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="text-xs text-[#7d8590] mb-1 block">Supplier *</label>
                <select value={poForm.supplier_id} onChange={e => setPoForm(f => ({ ...f, supplier_id: e.target.value }))}
                  className="w-full bg-[#161b22] border border-[#30363d] text-white rounded-md px-3 py-2 text-sm">
                  <option value="">Select supplier…</option>
                  {suppliers.map(s => <option key={s.supplier_id} value={s.supplier_id}>{s.name}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs text-[#7d8590] mb-1 block">Project (optional)</label>
                <select value={poForm.project_id} onChange={e => setPoForm(f => ({ ...f, project_id: e.target.value }))}
                  className="w-full bg-[#161b22] border border-[#30363d] text-white rounded-md px-3 py-2 text-sm">
                  <option value="">None</option>
                  {projects.map(p => <option key={p.project_id} value={p.project_id}>{p.name}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-[#7d8590] mb-1 block">Issue date *</label>
                  <input type="date" value={poForm.issue_date} onChange={e => setPoForm(f => ({ ...f, issue_date: e.target.value }))}
                    className="w-full bg-[#161b22] border border-[#30363d] text-white rounded-md px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="text-xs text-[#7d8590] mb-1 block">Expected delivery</label>
                  <input type="date" value={poForm.expected_date} onChange={e => setPoForm(f => ({ ...f, expected_date: e.target.value }))}
                    className="w-full bg-[#161b22] border border-[#30363d] text-white rounded-md px-3 py-2 text-sm" />
                </div>
              </div>
              <div>
                <label className="text-xs text-[#7d8590] mb-1 block">Notes</label>
                <textarea value={poForm.notes} onChange={e => setPoForm(f => ({ ...f, notes: e.target.value }))} rows={2}
                  className="w-full bg-[#161b22] border border-[#30363d] text-white rounded-md px-3 py-2 text-sm resize-none" />
              </div>
            </div>
            <div className="flex gap-2 mt-5">
              <Button onClick={createPo} disabled={saving} className="flex-1 bg-[#00F5D4] text-[#030712] hover:bg-[#00F5D4]/90 font-semibold">Create PO</Button>
              <Button onClick={() => setShowPoModal(false)} variant="outline" className="border-[#30363d] text-[#7d8590]">Cancel</Button>
            </div>
          </div>
        </div>
      )}

      {/* Supplier modal */}
      {showSupModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
          <div className="bg-[#0d1117] border border-[#21262d] rounded-xl w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-white font-semibold">{editSup ? "Edit Supplier" : "New Supplier"}</h2>
              <button onClick={() => { setShowSupModal(false); setEditSup(null); }} className="text-[#7d8590] hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <div className="space-y-4">
              {[["name", "Name *"], ["email", "Email"], ["phone", "Phone"], ["address", "Address"]].map(([k, lbl]) => (
                <div key={k}>
                  <label className="text-xs text-[#7d8590] mb-1 block">{lbl}</label>
                  <input value={supForm[k]} onChange={e => setSupForm(f => ({ ...f, [k]: e.target.value }))}
                    className="w-full bg-[#161b22] border border-[#30363d] text-white rounded-md px-3 py-2 text-sm" />
                </div>
              ))}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-[#7d8590] mb-1 block">Currency</label>
                  <select value={supForm.currency} onChange={e => setSupForm(f => ({ ...f, currency: e.target.value }))}
                    className="w-full bg-[#161b22] border border-[#30363d] text-white rounded-md px-3 py-2 text-sm">
                    {["GBP","USD","EUR"].map(c => <option key={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs text-[#7d8590] mb-1 block">Payment terms</label>
                  <input value={supForm.payment_terms} onChange={e => setSupForm(f => ({ ...f, payment_terms: e.target.value }))} placeholder="e.g. Net 30"
                    className="w-full bg-[#161b22] border border-[#30363d] text-white rounded-md px-3 py-2 text-sm" />
                </div>
              </div>
            </div>
            <div className="flex gap-2 mt-5">
              <Button onClick={saveSup} disabled={saving} className="flex-1 bg-[#00F5D4] text-[#030712] hover:bg-[#00F5D4]/90 font-semibold">
                {saving ? "Saving…" : (editSup ? "Save changes" : "Create supplier")}
              </Button>
              <Button onClick={() => { setShowSupModal(false); setEditSup(null); }} variant="outline" className="border-[#30363d] text-[#7d8590]">Cancel</Button>
            </div>
          </div>
        </div>
      )}
    </div></DarkPage>
  );
}
